'use client';

import { useState } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import { useTranslations } from 'next-intl';
import { Modal } from '@shared/ui/modal/Modal';
import { PrimaryButton } from '@shared/ui/button/PrimaryButton';
import { AuthButton } from '@shared/ui/button/AuthButton';
import { Input } from '@shared/ui/inputs/Input';
import { PasswordInput } from '@shared/ui/inputs/PasswordInput';
import { Typography } from '@shared/ui/typography/Typography';
import { Icon } from '@shared/ui/icons/Icon';
import { Checkbox } from '@shared/ui/checkbox/Checkbox';
import type { BaseModalProps } from '@shared/types/modal.types';
import { registerUser, loginWithGoogle } from '@entities/user/api';
import { useModals } from '@shared/hooks/useModal';
import { rateLimiter, RATE_LIMITS } from '@shared/lib/security/rate-limiter';
import { sanitizeEmail, sanitizeText } from '@shared/lib/security/sanitize';
import type { RegisterRequest } from '@hummii/shared-types';
import type { AuthModalIntent } from '@shared/types/modal.types';
import { useModal } from '@shared/hooks/useModal';
import { ActorType } from '@hummii/shared-types';
import { ShoppingBag, Briefcase } from 'lucide-react';
import { cn } from '@/src/shared/lib/utils';

interface RegistrationModalProps extends BaseModalProps {
  onSuccess?: () => void;
}

/**
 * Registration Modal - user registration form
 * Usage: const { open } = useModal('registration'); open({ intent: { intendedRole: UserRole.CONTRACTOR } });
 */
export function RegistrationModal({
  isOpen,
  onClose,
  onSuccess,
}: RegistrationModalProps) {
  // Get intent from modal props
  const { props: modalProps } = useModal('registration');
  const intent = modalProps?.intent as AuthModalIntent | undefined;
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    firstName: '',
    lastName: '',
    selectedRole: ActorType.CLIENT as ActorType, // Default to CLIENT
    // PIPEDA Compliance - Consent tracking
    termsAccepted: false,
    privacyAccepted: false,
    ageVerified: false,
    marketingConsent: false,
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const t = useTranslations('auth.registration');
  const { openModal } = useModals();

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    // Email validation
    if (!formData.email) {
      newErrors.email = t('errors.emailRequired');
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = t('errors.emailInvalid');
    }

    // Password validation (backend requires min 8 chars + complexity)
    if (!formData.password) {
      newErrors.password = t('errors.passwordRequired');
    } else if (formData.password.length < 8) {
      newErrors.password = t('errors.passwordTooShort');
    } else if (!/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d).{8,}$/.test(formData.password)) {
      newErrors.password = t('errors.passwordWeak');
    }

    // Confirm password
    if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = t('errors.passwordsMismatch');
    }

    // Name validation - commented out since firstName/lastName fields are not in the form
    // if (!formData.firstName.trim()) {
    //   newErrors.firstName = t('errors.firstNameRequired');
    // }

    // if (!formData.lastName.trim()) {
    //   newErrors.lastName = t('errors.lastNameRequired');
    // }

    // PIPEDA Compliance - Consent validation
    if (!formData.termsAccepted) {
      newErrors.termsAccepted = t('errors.termsRequired');
    }

    if (!formData.privacyAccepted) {
      newErrors.privacyAccepted = t('errors.privacyRequired');
    }

    if (!formData.ageVerified) {
      newErrors.ageVerified = t('errors.ageRequired');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const isValid = validateForm();
    if (!isValid) {
      return;
    }

    // Check rate limit
    const rateCheck = rateLimiter.checkLimit('register', RATE_LIMITS.REGISTER);
    if (!rateCheck.isAllowed) {
      setErrors({
        submit: t('errors.tooManyAttempts', {
          minutes: Math.ceil((rateCheck.retryAfter || 0) / 60)
        })
      });
      return;
    }

    setIsSubmitting(true);
    setErrors({}); // Clear previous errors

    try {
      // Sanitize email
      const sanitizedEmail = sanitizeEmail(formData.email);
      if (!sanitizedEmail) {
        setErrors({ email: t('errors.emailInvalid') });
        setIsSubmitting(false);
        return;
      }

      // Prepare registration data
      // Use email username as name since firstName/lastName are not in the form
      const emailUsername = sanitizedEmail.split('@')[0];
      const sanitizedName = sanitizeText(emailUsername);

      const registrationData: RegisterRequest = {
        email: sanitizedEmail,
        password: formData.password,
        name: sanitizedName, // User can update this later in profile
        role: formData.selectedRole, // Use selected role from form
        // PIPEDA Compliance - Consent data
        termsAccepted: formData.termsAccepted,
        privacyAccepted: formData.privacyAccepted,
        ageVerified: formData.ageVerified,
        marketingConsent: formData.marketingConsent,
      };

      // Call API
      await registerUser(registrationData);

      // Security: Don't log sensitive data in production
      if (process.env.NODE_ENV === 'development') {
        console.log('[RegistrationModal] Registration successful');
      }

      // Close registration modal
      onClose();

      // Open email confirmation modal
      openModal('confirm-email', { email: formData.email });

      // Call success callback
      onSuccess?.();

      // Execute intent callback if provided
      intent?.onSuccessAction?.();

      // Reset form
      setFormData({
        email: '',
        password: '',
        confirmPassword: '',
        firstName: '',
        lastName: '',
        selectedRole: ActorType.CLIENT,
        termsAccepted: false,
        privacyAccepted: false,
        ageVerified: false,
        marketingConsent: false,
      });
    } catch (error) {
      console.error('Registration failed:', error);

      // Handle specific error cases
      if (error instanceof Error) {
        // Check for specific error types
        if (error.message.includes('409')) {
          setErrors({ submit: t('errors.emailExists') });
        } else if (error.message.includes('400')) {
          setErrors({ submit: t('errors.invalidData') });
        } else {
          setErrors({ submit: t('errors.registrationFailed') });
        }
      } else {
        setErrors({ submit: t('errors.registrationFailed') });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    // Clear error for this field
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="xl"
      animation="fade"
      showCloseButton={false}
      className="!max-w-[1070px] !w-[1070px] !h-[550px] !rounded-[30px] overflow-hidden"
    >
      <div 
        className="flex flex-col md:flex-row h-full"
       
      >
        {/* Left Side - Decorative Image */}
        <div className="hidden md:flex md:w-1/2 items-center justify-center pl-[40px] py-8">
          <div className="relative w-full max-w-[420px] h-[420px]">
            <Image
              src="/images/modal/create-account.svg"
              alt="Create account illustration"
              fill
              className="object-contain"
              priority
            />
          </div>
        </div>

        {/* Right Side - Registration Form */}
        <div className="w-full md:w-1/2 p-6 md:py-8 md:pr-[40px]">
          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Title */}
            <div className="mb-6">
              <Typography variant="h2" className="mb-2">
                {t('title')}
              </Typography>
              {/* <Typography variant="bodySm" color="secondary">
                {t('subtitle')}
              </Typography> */}
            </div>
            
            {/* Email */}
            <div className="space-y-1">
              <Input
                id="email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                placeholder={t('fields.email')}
                error={!!errors.email}
                errorText={errors.email}
                leftIcon="email"
                leftIconColor="success"
              />
            </div>

            {/* Password */}
            <div className="space-y-1">
              <PasswordInput
                id="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                placeholder={t('fields.password')}
                error={!!errors.password}
                errorText={errors.password}
              />
            </div>

            {/* Confirm Password */}
            <div className="space-y-1">
              <PasswordInput
                id="confirmPassword"
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleChange}
                placeholder={t('fields.confirmPassword')}
                error={!!errors.confirmPassword}
                errorText={errors.confirmPassword}
              />
            </div>

            {/* Role Selector */}
            <div className="space-y-2">
              <Typography variant="bodySm" color="secondary" className="px-1">
                {t('fields.roleLabel')}
              </Typography>
              <div className="grid grid-cols-2 gap-3">
                {/* Client Option */}
                <button
                  type="button"
                  onClick={() => setFormData(prev => ({ ...prev, selectedRole: ActorType.CLIENT }))}
                  className={cn(
                    'flex flex-col items-center gap-2 p-4 rounded-lg border-2 transition-all',
                    formData.selectedRole === ActorType.CLIENT
                      ? 'border-accent-1 bg-white'
                      : 'border-border-primary bg-transparent hover:border-accent-1/50'
                  )}
                >
                  <ShoppingBag
                    className={cn(
                      'h-6 w-6',
                      formData.selectedRole === ActorType.CLIENT
                        ? 'text-accent-1'
                        : 'text-text-secondary'
                    )}
                  />
                  <Typography
                    variant="bodySm"
                    weight="semibold"
                    className={cn(
                      formData.selectedRole === ActorType.CLIENT
                        ? 'text-accent-1'
                        : 'text-text-primary'
                    )}
                  >
                    {t('fields.roleClient')}
                  </Typography>
                  <Typography
                    variant="note"
                    color="tertiary"
                    className="text-center"
                  >
                    {t('fields.roleClientDesc')}
                  </Typography>
                </button>

                {/* Worker Option */}
                <button
                  type="button"
                  onClick={() => setFormData(prev => ({ ...prev, selectedRole: ActorType.WORKER }))}
                  className={cn(
                    'flex flex-col items-center gap-2 p-4 rounded-lg border-2 transition-all',
                    formData.selectedRole === ActorType.WORKER
                      ? 'border-accent-1 bg-white'
                      : 'border-border-primary bg-transparent hover:border-accent-1/50'
                  )}
                >
                  <Briefcase
                    className={cn(
                      'h-6 w-6',
                      formData.selectedRole === ActorType.WORKER
                        ? 'text-accent-1'
                        : 'text-text-secondary'
                    )}
                  />
                  <Typography
                    variant="bodySm"
                    weight="semibold"
                    className={cn(
                      formData.selectedRole === ActorType.WORKER
                        ? 'text-accent-1'
                        : 'text-text-primary'
                    )}
                  >
                    {t('fields.roleWorker')}
                  </Typography>
                  <Typography
                    variant="note"
                    color="tertiary"
                    className="text-center"
                  >
                    {t('fields.roleWorkerDesc')}
                  </Typography>
                </button>
              </div>
            </div>

            {/* PIPEDA Compliance - Consent Checkboxes */}
            <div className="space-y-3 pt-4 border-t border-border-primary">
              {/* Terms of Service */}
              <div className="flex items-start gap-2">
                <Checkbox
                  id="termsAccepted"
                  checked={formData.termsAccepted}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, termsAccepted: e.target.checked }))
                  }
                />
                <label htmlFor="termsAccepted" className="flex-1 cursor-pointer">
                  <Typography variant="bodySm">
                    {t('fields.termsLabel')}{' '}
                    <Link
                      href="/legal/terms"
                      target="_blank"
                      className="text-accent-1 hover:underline"
                      onClick={(e) => e.stopPropagation()}
                    >
                      {t('fields.termsLink')}
                    </Link>
                    <span className="text-feedback-error"> *</span>
                  </Typography>
                  {errors.termsAccepted && (
                    <Typography variant="note" color="error" className="mt-1">
                      {errors.termsAccepted}
                    </Typography>
                  )}
                </label>
              </div>

              {/* Privacy Policy */}
              <div className="flex items-start gap-2">
                <Checkbox
                  id="privacyAccepted"
                  checked={formData.privacyAccepted}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, privacyAccepted: e.target.checked }))
                  }
                />
                <label htmlFor="privacyAccepted" className="flex-1 cursor-pointer">
                  <Typography variant="bodySm">
                    {t('fields.privacyLabel')}{' '}
                    <Link
                      href="/legal/privacy"
                      target="_blank"
                      className="text-accent-1 hover:underline"
                      onClick={(e) => e.stopPropagation()}
                    >
                      {t('fields.privacyLink')}
                    </Link>
                    <span className="text-feedback-error"> *</span>
                  </Typography>
                  {errors.privacyAccepted && (
                    <Typography variant="note" color="error" className="mt-1">
                      {errors.privacyAccepted}
                    </Typography>
                  )}
                </label>
              </div>

              {/* Age Verification */}
              <div className="flex items-start gap-2">
                <Checkbox
                  id="ageVerified"
                  checked={formData.ageVerified}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, ageVerified: e.target.checked }))
                  }
                />
                <label htmlFor="ageVerified" className="flex-1 cursor-pointer">
                  <Typography variant="bodySm">
                    {t('fields.ageLabel')}
                    <span className="text-feedback-error"> *</span>
                  </Typography>
                  {errors.ageVerified && (
                    <Typography variant="note" color="error" className="mt-1">
                      {errors.ageVerified}
                    </Typography>
                  )}
                </label>
              </div>

              {/* Marketing Consent (Optional) */}
              <div className="flex items-start gap-2">
                <Checkbox
                  id="marketingConsent"
                  checked={formData.marketingConsent}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, marketingConsent: e.target.checked }))
                  }
                />
                <label htmlFor="marketingConsent" className="flex-1 cursor-pointer">
                  <Typography variant="bodySm" color="secondary">
                    {t('fields.marketingLabel')}
                  </Typography>
                </label>
              </div>
            </div>

            {/* Submit Error */}
            {errors.submit && (
              <div className="p-3 bg-feedback-error/10 border border-feedback-error rounded-lg">
                <Typography variant="bodySm" color="error">
                  {errors.submit}
                </Typography>
              </div>
            )}

            {/* Actions */}
            <div className="space-y-4 pt-4">
              <PrimaryButton
                type="submit"
                fullWidth
                isLoading={isSubmitting}
                disabled={isSubmitting}
              >
                {isSubmitting ? t('buttons.creatingAccount') : t('buttons.createAccount')}
              </PrimaryButton>

              {/* Divider */}
              <div className="flex items-center gap-3">
                <div className="flex-1 h-[1px] bg-border-secondary" />
                <Typography variant="bodySm" color="tertiary">
                  {t('buttons.or')}
                </Typography>
                <div className="flex-1 h-[1px] bg-border-secondary" />
              </div>

              {/* Social Login Buttons */}
              <div className="flex flex-row gap-3">
                <AuthButton
                  variant="secondary"
                  fullWidth
                  onClick={() => loginWithGoogle()}
                >
                  <Icon name="google" size="md" color="inherit" />
                  {t('buttons.google')}
                </AuthButton>
              </div>

              {/* Login Link */}
              <div className="text-center pt-2">
                <Typography variant="bodySm" color="secondary" className="inline">
                  {t('footer.hasAccount')}{' '}
                </Typography>
                <button
                  type="button"
                  onClick={() => {
                    onClose();
                    openModal('login');
                  }}
                  className="inline bg-none border-none p-0 cursor-pointer hover:underline"
                >
                  <Typography 
                    variant="bodySm" 
                    color="accent" 
                    weight="semibold"
                    className="inline"
                  >
                    {t('footer.login')}
                  </Typography>
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </Modal>
  );
}
